import terminologyService from '../services/terminology'
import { createSlice } from '@reduxjs/toolkit'
import { setSuccessNotification, setErrorNotification } from './notificationReducer'

const initialState = {
    results: [],
    count: 0,
    next: null,
    previous: null,
    total_pages: 1,
    current_page: 1,
    loading: false
}

const revisionSlice = createSlice({
    name: 'revisions',
    initialState,
    reducers: {
        setRevisions(state, action) {
            return action.payload
        },
        setLoading(state, action) {
            state.loading = action.payload
        },
        appendRevision(state, action) {
            if (state.results) {
                state.results.push(action.payload)
                state.count += 1
            }
        },
        destroyRevision(state, action) {
            if (state.results) {
                state.results = state.results.filter(terminology => terminology.id !== action.payload)
                state.count -= 1
            }
        }
    }
})

export const { setRevisions, setLoading, appendRevision, destroyRevision } = revisionSlice.actions

// Last-issued-wins (issue #407): TerminologyAdmin's revisions tab is paginated, and
// without this the slowest response, not the newest request, decided what showed.
let latestRevisionsRequest = 0

export const createRevision = (data, isAdminOrTerminologyAdmin) => {
    return async dispatch => {
        try {
            const revision = await terminologyService.createRevision(data)
            if (isAdminOrTerminologyAdmin) {
                dispatch(appendRevision(revision))
            }
            dispatch(setSuccessNotification('Revision submitted successfully'))
        } catch (error) {
            let errorMessage = 'Error submitting revision, please try again later'

            if (error.response && error.response.data) {
                const { detail } = error.response.data
                if (detail && Array.isArray(detail)) {
                    errorMessage = detail.join(', ')
                } else if (detail) {
                    errorMessage = detail
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message
                }
            }

            dispatch(setErrorNotification(`Error submitting revision: ${errorMessage}`))
        }
    }
}

// will only be used for admin since users cant see revisions
export const initializeRevisions = (page = 1, ordering = null) => {
    return async dispatch => {
        const requestId = ++latestRevisionsRequest
        try {
            dispatch(setLoading(true))
            const revisions = await terminologyService.retrieveRevisions(page, ordering)
            if (requestId !== latestRevisionsRequest) return
            dispatch(setRevisions({...revisions, loading: false}))
        } catch {
            if (requestId !== latestRevisionsRequest) return // a superseded failure must not toast or clear the newer request's spinner
            dispatch(setErrorNotification('Error retrieving terminology'))
            dispatch(setLoading(false))
        }
    }
}

export const deleteRevision = (id, isAdminOrTerminologyAdmin) => {
    return async dispatch => {
        if (isAdminOrTerminologyAdmin) {
            try {
                await terminologyService.destroyRevision(id)
                dispatch(destroyRevision(id))
                dispatch(setSuccessNotification('Revision deleted successfully'))
            } catch {
                dispatch(setErrorNotification('Error deleting revision'))
            }
        } else {
            dispatch(setErrorNotification('Permission Denied: You must be a staff member or terminology admin to perform this action'))
        }
    }
}

// Deletes the now-applied revision and (server-side) emails its submitter --
// the frontend must PUT the revision's contents onto the term first (see
// AdminTerminologyRevisionModal's handleAccept) and only call this once that
// succeeded. Returns a boolean so the caller can keep the modal open on failure.
export const acceptRevision = (id, isAdminOrTerminologyAdmin) => {
    return async dispatch => {
        if (isAdminOrTerminologyAdmin) {
            try {
                await terminologyService.acceptRevision(id)
                dispatch(destroyRevision(id))
                dispatch(setSuccessNotification('Revision accepted'))
                return true
            } catch {
                dispatch(setErrorNotification('Error accepting revision'))
                return false
            }
        } else {
            dispatch(setErrorNotification('Permission Denied: You must be a staff member or terminology admin to perform this action'))
            return false
        }
    }
}

export default revisionSlice.reducer
